
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';

// Type definitions
interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  type: string;
  automat_id?: string;
  automat_name?: string;
  kontakt_id?: string;
  kontakt_name?: string;
  priority: string;
  status: string;
  assigned_to: string;
  estimated_duration: number;
  notes: string;
  recurring: boolean;
  recurring_pattern?: string;
}

interface Automat {
  id: string;
  name: string;
  location: string;
}

interface Kontakt {
  id: string;
  name: string;
  firma: string;
}

// Constants
const eventTypes = [
  { value: 'wartung', label: 'Wartung', icon: 'ri-tools-line', color: 'bg-blue-100 text-blue-800' },
  { value: 'reparatur', label: 'Reparatur', icon: 'ri-hammer-line', color: 'bg-red-100 text-red-800' },
  { value: 'inspektion', label: 'Inspektion', icon: 'ri-search-line', color: 'bg-green-100 text-green-800' },
  { value: 'termin', label: 'Termin', icon: 'ri-calendar-line', color: 'bg-purple-100 text-purple-800' },
  { value: 'meeting', label: 'Meeting', icon: 'ri-group-line', color: 'bg-orange-100 text-orange-800' },
  { value: 'schulung', label: 'Schulung', icon: 'ri-book-line', color: 'bg-indigo-100 text-indigo-800' }
];

const priorities = [
  { value: 'niedrig', label: 'Niedrig', color: 'bg-green-100 text-green-800' },
  { value: 'mittel', label: 'Mittel', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'hoch', label: 'Hoch', color: 'bg-red-100 text-red-800' }
];

const statuses = [
  { value: 'geplant', label: 'Geplant', color: 'bg-blue-100 text-blue-800' },
  { value: 'in_bearbeitung', label: 'In Bearbeitung', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'abgeschlossen', label: 'Abgeschlossen', color: 'bg-green-100 text-green-800' },
  { value: 'verschoben', label: 'Verschoben', color: 'bg-orange-100 text-orange-800' },
  { value: 'abgesagt', label: 'Abgesagt', color: 'bg-red-100 text-red-800' }
];

const recurringPatterns = [
  { value: 'taglich', label: 'Täglich' },
  { value: 'wochentlich', label: 'Wöchentlich' },
  { value: 'monatlich', label: 'Monatlich' },
  { value: 'jahrlich', label: 'Jährlich' }
];

export default function Kalender() {
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [automaten, setAutomaten] = useState<Automat[]>([]);
  const [kontakte, setKontakte] = useState<Kontakt[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [viewingEvent, setViewingEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filterType, setFilterType] = useState('');
  const [filterAutomat, setFilterAutomat] = useState('');
  const [selectedDate, setSelectedDate] = useState<string>('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    time: '09:00',
    type: 'wartung' as const,
    automat_id: '',
    kontakt_id: '',
    priority: 'mittel' as const,
    status: 'geplant' as const,
    assigned_to: '',
    estimated_duration: '60',
    notes: '',
    recurring: false,
    recurring_pattern: 'monatlich' as const
  });

  // Load data from Supabase
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      // Load events from kalender table
      const { data: eventsData, error: eventsError } = await supabase
        .from('kalender')
        .select('*')
        .eq('user_id', user?.id)
        .order('date', { ascending: true });

      if (eventsError) throw eventsError;

      const eventList = eventsData.map(item => ({
        id: item.id,
        title: item.title,
        description: item.description || '',
        date: item.date,
        time: item.time,
        type: item.type,
        automat_id: item.automat_id,
        automat_name: item.automat_name,
        kontakt_id: item.kontakt_id,
        kontakt_name: item.kontakt_name,
        priority: item.priority,
        status: item.status,
        assigned_to: item.assigned_to || '',
        estimated_duration: item.estimated_duration || 60,
        notes: item.notes || '',
        recurring: item.recurring || false,
        recurring_pattern: item.recurring_pattern
      }));

      setEvents(eventList);

      // Load ONLY REAL AUTOMATEN (exclude references) from daten table
      const { data: automatenData, error: automatenError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'automat');

      if (automatenError) throw automatenError;

      // Filter out references - only keep physical automaten
      const automatenList = automatenData
        .filter(item => {
          // Only include real physical automaten, exclude references
          return item.data && item.data.machine_type === 'physical_automat';
        })
        .map(item => ({
          id: item.id,
          name: item.data.name,
          location: item.data.location
        }));

      setAutomaten(automatenList);

      // Load kontakte from daten table
      const { data: kontakteData, error: kontakteError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'contact');

      if (kontakteError) throw kontakteError;

      const kontakteList = kontakteData.map(item => ({
        id: item.id,
        name: item.data.name,
        firma: item.data.firma || ''
      }));

      setKontakte(kontakteList);

    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      date: selectedDate || new Date().toISOString().split('T')[0],
      time: '09:00',
      type: 'wartung',
      automat_id: '',
      kontakt_id: '',
      priority: 'mittel',
      status: 'geplant',
      assigned_to: '',
      estimated_duration: '60',
      notes: '',
      recurring: false,
      recurring_pattern: 'monatlich'
    });
    setEditingEvent(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const selectedAutomat = automaten.find(a => a.id === formData.automat_id);
    const selectedKontakt = kontakte.find(k => k.id === formData.kontakt_id);
    
    const eventData = {
      user_id: user?.id,
      title: formData.title,
      description: formData.description,
      date: formData.date,
      time: formData.time,
      type: formData.type,
      automat_id: formData.automat_id || null,
      automat_name: selectedAutomat?.name || null,
      kontakt_id: formData.kontakt_id || null,
      kontakt_name: selectedKontakt ? `${selectedKontakt.name}${selectedKontakt.firma ? ` (${selectedKontakt.firma})` : ''}` : null,
      priority: formData.priority,
      status: formData.status,
      assigned_to: formData.assigned_to,
      estimated_duration: parseInt(formData.estimated_duration),
      notes: formData.notes,
      recurring: formData.recurring,
      recurring_pattern: formData.recurring ? formData.recurring_pattern : null
    };

    try {
      const { data, error } = await supabase
        .from('kalender')
        .insert(eventData)
        .select()
        .single();

      if (error) throw error;

      setEvents(prev => [{
        id: data.id,
        title: data.title,
        description: data.description || '',
        date: data.date,
        time: data.time,
        type: data.type,
        automat_id: data.automat_id,
        automat_name: data.automat_name,
        kontakt_id: data.kontakt_id,
        kontakt_name: data.kontakt_name,
        priority: data.priority,
        status: data.status,
        assigned_to: data.assigned_to || '',
        estimated_duration: data.estimated_duration || 60,
        notes: data.notes || '',
        recurring: data.recurring || false,
        recurring_pattern: data.recurring_pattern
      }, ...prev]);

      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error adding event:', error);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingEvent) return;

    const selectedAutomat = automaten.find(a => a.id === formData.automat_id);
    const selectedKontakt = kontakte.find(k => k.id === formData.kontakt_id);
    
    const eventData = {
      title: formData.title,
      description: formData.description,
      date: formData.date,
      time: formData.time,
      type: formData.type,
      automat_id: formData.automat_id || null,
      automat_name: selectedAutomat?.name || null,
      kontakt_id: formData.kontakt_id || null,
      kontakt_name: selectedKontakt ? `${selectedKontakt.name}${selectedKontakt.firma ? ` (${selectedKontakt.firma})` : ''}` : null,
      priority: formData.priority,
      status: formData.status,
      assigned_to: formData.assigned_to,
      estimated_duration: parseInt(formData.estimated_duration),
      notes: formData.notes,
      recurring: formData.recurring,
      recurring_pattern: formData.recurring ? formData.recurring_pattern : null,
      updated_at: new Date().toISOString()
    };

    try {
      const { error } = await supabase
        .from('kalender')
        .update(eventData)
        .eq('id', editingEvent.id);

      if (error) throw error;

      setEvents(prev => prev.map(event => 
        event.id === editingEvent.id 
          ? { 
              id: editingEvent.id, 
              title: eventData.title,
              description: eventData.description || '',
              date: eventData.date,
              time: eventData.time,
              type: eventData.type,
              automat_id: eventData.automat_id,
              automat_name: eventData.automat_name,
              kontakt_id: eventData.kontakt_id,
              kontakt_name: eventData.kontakt_name,
              priority: eventData.priority,
              status: eventData.status,
              assigned_to: eventData.assigned_to || '',
              estimated_duration: eventData.estimated_duration,
              notes: eventData.notes || '',
              recurring: eventData.recurring,
              recurring_pattern: eventData.recurring_pattern
            }
          : event
      ));

      setIsEditModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error updating event:', error);
    }
  };

  const openViewModal = (event: Event) => {
    setViewingEvent(event);
    setIsViewModalOpen(true);
  };

  const openEditModal = (event: Event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      date: event.date,
      time: event.time,
      type: event.type,
      automat_id: event.automat_id || '',
      kontakt_id: event.kontakt_id || '',
      priority: event.priority,
      status: event.status,
      assigned_to: event.assigned_to,
      estimated_duration: event.estimated_duration.toString(),
      notes: event.notes,
      recurring: event.recurring,
      recurring_pattern: event.recurring_pattern || 'monatlich'
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Termin wirklich löschen?')) return;

    try {
      const { error } = await supabase
        .from('kalender')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setEvents(prev => prev.filter(event => event.id !== id));
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  // Calendar functions
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1; // Monday = 0
  };

  const getEventsForDate = (date: string) => {
    return events.filter(event => event.date === date);
  };

  const formatDate = (year: number, month: number, day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const handleDateClick = (dateStr: string) => {
    setSelectedDate(dateStr);
    setFormData(prev => ({ ...prev, date: dateStr }));
    setIsModalOpen(true);
  };

  const getTypeInfo = (type: string) => {
    return eventTypes.find(t => t.value === type) || eventTypes[0];
  };

  const getPriorityInfo = (priority: string) => {
    return priorities.find(p => p.value === priority) || priorities[1];
  };

  const getStatusInfo = (status: string) => {
    return statuses.find(s => s.value === status) || statuses[0];
  };

  const filteredEvents = events.filter(event => {
    const matchesType = !filterType || event.type === filterType;
    const matchesAutomat = !filterAutomat || event.automat_id === filterAutomat;
    return matchesType && matchesAutomat;
  });

  // Generate calendar days
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const today = new Date().toISOString().split('T')[0];

  const calendarDays = [];
  
  // Previous month days
  const prevMonth = new Date(year, month - 1, 0);
  for (let i = firstDay - 1; i >= 0; i--) {
    const day = prevMonth.getDate() - i;
    const dateStr = formatDate(prevMonth.getFullYear(), prevMonth.getMonth(), day);
    calendarDays.push({
      day,
      dateStr,
      isCurrentMonth: false,
      isToday: false,
      events: getEventsForDate(dateStr)
    });
  }

  // Current month days
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = formatDate(year, month, day);
    calendarDays.push({
      day,
      dateStr,
      isCurrentMonth: true,
      isToday: dateStr === today,
      events: getEventsForDate(dateStr)
    });
  }

  // Next month days
  const totalCells = Math.ceil(calendarDays.length / 7) * 7;
  const nextMonth = new Date(year, month + 1, 1);
  let nextDay = 1;
  while (calendarDays.length < totalCells) {
    const dateStr = formatDate(nextMonth.getFullYear(), nextMonth.getMonth(), nextDay);
    calendarDays.push({
      day: nextDay,
      dateStr,
      isCurrentMonth: false,
      isToday: false,
      events: getEventsForDate(dateStr)
    });
    nextDay++;
  }

  const monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Lade Kalender...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="bg-white rounded-lg shadow-sm border w-full overflow-x-hidden">
          <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200">
            <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex items-center">
                  Kalender
                  <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-blue-600">
                    <i className="ri-cloud-line mr-1"></i>
                    Supabase Kalender-Tabelle
                  </span>
                </h1>
                <p className="text-gray-600 mt-1 text-xs sm:text-base break-words">
                  Wartung & Termine verwalten ({events.length} Termine)
                </p>
              </div>
              <button
                onClick={() => {
                  resetForm();
                  setIsModalOpen(true);
                }}
                className="w-full sm:w-auto bg-blue-600 text-white px-3 sm:px-4 py-2.5 sm:py-2 rounded-md hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center flex-shrink-0 text-sm sm:text-sm font-medium"
              >
                <i className="ri-add-line w-4 h-4 sm:w-4 sm:h-4 flex items-center justify-center mr-2"></i>
                Neuer Termin
              </button>
            </div>
          </div>

          {/* Calendar Navigation */}
          <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2 sm:space-x-4">
                <button
                  onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
                  className="p-2 text-gray-600 hover:text-gray-900 cursor-pointer touch-manipulation"
                >
                  <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                </button>
                <h2 className="text-base sm:text-lg sm:text-xl font-semibold text-gray-900">
                  {monthNames[month]} {year}
                </h2>
                <button
                  onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
                  className="p-2 text-gray-600 hover:text-gray-900 cursor-pointer touch-manipulation"
                >
                  <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
                </button>
              </div>
              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-3 py-2 text-xs sm:text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 cursor-pointer whitespace-nowrap touch-manipulation"
              >
                Heute
              </button>
            </div>

            {/* Filter */}
            {events.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="flex-1 sm:flex-none px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm pr-8"
                >
                  <option value="">Alle Termine</option>
                  {eventTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                
                <select
                  value={filterAutomat}
                  onChange={(e) => setFilterAutomat(e.target.value)}
                  className="flex-1 sm:flex-none px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm pr-8"
                >
                  <option value="">Alle Automaten</option>
                  {automaten.map(automat => (
                    <option key={automat.id} value={automat.id}>
                      {automat.name} - {automat.location}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Calendar Grid */}
          <div className="p-2 sm:p-4 lg:p-6 w-full overflow-x-hidden">
            <div className="bg-white rounded-lg border">
              {/* Week header */}
              <div className="grid grid-cols-7 border-b">
                {weekDays.map(day => (
                  <div key={day} className="p-2 sm:p-3 text-center text-xs sm:text-sm font-medium text-gray-500 border-r last:border-r-0">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar days */}
              <div className="grid grid-cols-7">
                {calendarDays.map((calendarDay, index) => {
                  const dayEvents = filteredEvents.filter(event => event.date === calendarDay.dateStr);
                  
                  return (
                    <div
                      key={index}
                      className={`min-h-[100px] sm:min-h-[140px] p-1 sm:p-2 border-r border-b last:border-r-0 cursor-pointer hover:bg-gray-50 transition-colors ${
                        !calendarDay.isCurrentMonth ? 'bg-gray-50 text-gray-400' : ''
                      } ${calendarDay.isToday ? 'bg-blue-50 border-blue-200' : ''} touch-manipulation`}
                      onClick={() => handleDateClick(calendarDay.dateStr)}
                    >
                      {/* Day number */}
                      <div className={`text-sm sm:text-base font-medium mb-1 sm:mb-2 flex items-center justify-between ${
                        calendarDay.isToday ? 'text-blue-600' : calendarDay.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                      }`}>
                        <span>{calendarDay.day}</span>
                        {dayEvents.length > 0 && (
                          <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-medium">
                            {dayEvents.length}
                          </span>
                        )}
                      </div>
                      
                      {/* Events */}
                      <div className="space-y-1">
                        {dayEvents.slice(0, 3).map(event => {
                          const typeInfo = getTypeInfo(event.type);
                          const priorityInfo = getPriorityInfo(event.priority);
                          
                          // Prioritätsbasierte Farben mit besserer Sichtbarkeit
                          let eventColor = 'bg-green-500 text-white'; // Niedrig = Grün
                          if (event.priority === 'mittel') {
                            eventColor = 'bg-yellow-500 text-white'; // Mittel = Gelb
                          } else if (event.priority === 'hoch') {
                            eventColor = 'bg-red-500 text-white'; // Hoch = Rot
                          }
                          
                          return (
                            <div
                              key={event.id}
                              className={`text-xs p-1.5 sm:p-2 rounded-md cursor-pointer transition-all hover:shadow-sm ${eventColor} touch-manipulation`}
                              onClick={(e) => {
                                e.stopPropagation();
                                openViewModal(event);
                              }}
                              title={`${event.title} - ${event.time} (${priorityInfo.label})`}
                            >
                              <div className="flex items-center space-x-1">
                                <i className={`${typeInfo.icon} text-xs flex-shrink-0`}></i>
                                <span className="font-medium truncate">
                                  {event.title.length > 12 ? event.title.substring(0, 12) + '...' : event.title}
                                </span>
                              </div>
                              <div className="text-xs opacity-90 mt-0.5">
                                {event.time}
                              </div>
                              {event.automatName && (
                                <div className="text-xs opacity-80 truncate">
                                  {event.automatName.length > 15 ? event.automatName.substring(0, 15) + '...' : event.automatName}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        
                        {/* More events indicator */}
                        {dayEvents.length > 3 && (
                          <div 
                            className="text-xs text-center py-1 text-gray-500 hover:text-gray-700 cursor-pointer bg-gray-100 rounded-md transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Show all events for this day in a modal or expand view
                              const allDayEvents = dayEvents.slice(3);
                              if (allDayEvents.length > 0) {
                                openViewModal(allDayEvents[0]); // For now, show first additional event
                              }
                            }}
                          >
                            +{dayEvents.length - 3} weitere
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mobile: Compact Event List for Today */}
              <div className="block sm:hidden border-t border-gray-200 bg-gray-50">
                <div className="p-3">
                  <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                    <i className="ri-calendar-check-line mr-2"></i>
                    Heute ({new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })})
                  </h4>
                  {(() => {
                    const todayEvents = filteredEvents.filter(event => event.date === today);
                    if (todayEvents.length === 0) {
                      return (
                        <p className="text-xs text-gray-500">Keine Termine heute</p>
                      );
                    }
                    return (
                      <div className="space-y-2">
                        {todayEvents.slice(0, 3).map(event => {
                          const typeInfo = getTypeInfo(event.type);
                          const priorityInfo = getPriorityInfo(event.priority);
                          
                          let eventColor = 'border-green-200 bg-green-50'; // Niedrig
                          if (event.priority === 'mittel') {
                            eventColor = 'border-yellow-200 bg-yellow-50'; // Mittel
                          } else if (event.priority === 'hoch') {
                            eventColor = 'border-red-200 bg-red-50'; // Hoch
                          }
                          
                          return (
                            <div
                              key={event.id}
                              className={`p-2 rounded-lg border cursor-pointer ${eventColor} touch-manipulation`}
                              onClick={() => openViewModal(event)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2 min-w-0 flex-1">
                                  <i className={`${typeInfo.icon} text-sm flex-shrink-0 ${
                                    event.priority === 'hoch' ? 'text-red-600' : 
                                    event.priority === 'mittel' ? 'text-yellow-600' : 'text-green-600'
                                  }`}></i>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 truncate">{event.title}</p>
                                    {event.automatName && (
                                      <p className="text-xs text-gray-600 truncate">{event.automatName}</p>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <p className="text-sm font-medium text-gray-900">{event.time}</p>
                                  <p className={`text-xs font-medium ${
                                    event.priority === 'hoch' ? 'text-red-600' : 
                                    event.priority === 'mittel' ? 'text-yellow-600' : 'text-green-600'
                                  }`}>
                                    {priorityInfo.label}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {todayEvents.length > 3 && (
                          <div className="text-center">
                            <button
                              onClick={() => {
                                // Scroll to upcoming events section
                                const upcomingSection = document.querySelector('[data-upcoming-events]');
                                if (upcomingSection) {
                                  upcomingSection.scrollIntoView({ behavior: 'smooth' });
                                }
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                              +{todayEvents.length - 3} weitere Termine anzeigen
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Upcoming Events List */}
              {filteredEvents.length > 0 && (
                <div className="mt-6 sm:mt-8" data-upcoming-events>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Kommende Termine</h3>
                  <div className="space-y-2 sm:space-y-3">
                    {filteredEvents
                      .filter(event => new Date(event.date + 'T' + event.time) >= new Date())
                      .sort((a, b) => new Date(a.date + 'T' + a.time).getTime() - new Date(b.date + 'T' + b.time).getTime())
                      .slice(0, 8)
                      .map((event) => {
                        const typeInfo = getTypeInfo(event.type);
                        const priorityInfo = getPriorityInfo(event.priority);
                        const statusInfo = getStatusInfo(event.status);
                        
                        // Bessere Farbgebung für bessere Sichtbarkeit
                        let cardBorder = 'border-green-200'; // Niedrig
                        let cardBg = 'bg-white hover:bg-green-50';
                        if (event.priority === 'mittel') {
                          cardBorder = 'border-yellow-300';
                          cardBg = 'bg-white hover:bg-yellow-50';
                        } else if (event.priority === 'hoch') {
                          cardBorder = 'border-red-300';
                          cardBg = 'bg-white hover:bg-red-50';
                        }
                        
                        return (
                          <div key={event.id} className={`${cardBg} rounded-lg shadow-sm border-2 ${cardBorder} p-3 sm:p-4 transition-all duration-200`}>
                            <div className="flex items-start justify-between">
                              <div 
                                className="flex items-start space-x-3 flex-1 cursor-pointer touch-manipulation"
                                onClick={() => openViewModal(event)}
                              >
                                <div className={`p-2.5 rounded-lg ${typeInfo.color.replace('text-', 'bg-').replace('800', '100')} border ${typeInfo.color.replace('100', '200')}`}>
                                  <i className={`${typeInfo.icon} text-lg ${typeInfo.color.replace('100', '600')}`}></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">{event.title}</h4>
                                  {event.description && (
                                    <p className="text-xs sm:text-sm text-gray-600 mb-2 line-clamp-2">{event.description}</p>
                                  )}
                                  
                                  {/* Enhanced badges */}
                                  <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3">
                                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${typeInfo.color} ${typeInfo.color.replace('text-', 'border-').replace('800', '300')}`}>
                                      <i className={`${typeInfo.icon} mr-1`}></i>
                                      {typeInfo.label}
                                    </span>
                                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${priorityInfo.color} ${priorityInfo.color.replace('text-', 'border-').replace('800', '300')}`}>
                                      {event.priority === 'hoch' && <i className="ri-alarm-warning-line mr-1"></i>}
                                      {event.priority === 'mittel' && <i className="ri-time-line mr-1"></i>}
                                      {event.priority === 'niedrig' && <i className="ri-check-line mr-1"></i>}
                                      {priorityInfo.label}
                                    </span>
                                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${statusInfo.color} ${statusInfo.color.replace('text-', 'border-').replace('800', '300')}`}>
                                      {statusInfo.label}
                                    </span>
                                  </div>
                                  
                                  {/* Enhanced info grid */}
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                    <div className="bg-gray-50 rounded-lg p-2">
                                      <span className="text-gray-500 block">Datum:</span>
                                      <p className="font-semibold text-gray-900">{new Date(event.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-2">
                                      <span className="text-gray-500 block">Zeit:</span>
                                      <p className="font-semibold text-gray-900">{event.time}</p>
                                    </div>
                                    {event.automatName && (
                                      <div className="bg-blue-50 rounded-lg p-2 col-span-2 sm:col-span-1">
                                        <span className="text-blue-600 block">Automat:</span>
                                        <p className="font-semibold text-blue-900 truncate" title={event.automatName}>{event.automatName}</p>
                                      </div>
                                    )}
                                    {event.kontaktName && (
                                      <div className="bg-purple-50 rounded-lg p-2 col-span-2 sm:col-span-1">
                                        <span className="text-purple-600 block">Kontakt:</span>
                                        <p className="font-semibold text-purple-900 truncate" title={event.kontaktName}>{event.kontaktName}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row items-center space-y-1 sm:space-y-0 sm:space-x-2 ml-2">
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditModal(event);
                                  }}
                                  className="text-gray-400 hover:text-blue-600 cursor-pointer p-2 rounded-md hover:bg-blue-50 touch-manipulation transition-colors"
                                  title="Bearbeiten"
                                >
                                  <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
                                </button>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(event.id);
                                  }}
                                  className="text-gray-400 hover:text-red-600 cursor-pointer p-2 rounded-md hover:bg-red-50 touch-manipulation transition-colors"
                                  title="Löschen"
                                >
                                  <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Modal für Terminansicht */}
          <Modal
            isOpen={isViewModalOpen}
            onClose={() => {
              setIsViewModalOpen(false);
              setViewingEvent(null);
            }}
            title="Termindetails"
            maxWidth="xl"
          >
            {viewingEvent && (
              <div className="space-y-4 sm:space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0">
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className={`p-3 rounded-lg ${getTypeInfo(viewingEvent.type).color.replace('text-', 'bg-').replace('800', '100')}`}>
                      <i className={`${getTypeInfo(viewingEvent.type).icon} text-xl sm:text-2xl ${getTypeInfo(viewingEvent.type).color.replace('100', '600')}`}></i>
                    </div>
                    <div>
                      <h2 className="text-lg sm:text-xl font-bold text-gray-900">{viewingEvent.title}</h2>
                      <p className="text-gray-600 mt-1">{viewingEvent.description}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-3 py-1 text-xs sm:text-sm font-medium rounded-full ${getTypeInfo(viewingEvent.type).color}`}>
                      {getTypeInfo(viewingEvent.type).label}
                    </span>
                    <span className={`px-3 py-1 text-xs sm:text-sm font-medium rounded-full ${getPriorityInfo(viewingEvent.priority).color}`}>
                      {getPriorityInfo(viewingEvent.priority).label}
                    </span>
                    <span className={`px-3 py-1 text-xs sm:text-sm font-medium rounded-full ${getStatusInfo(viewingEvent.status).color}`}>
                      {getStatusInfo(viewingEvent.status).label}
                    </span>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Datum & Zeit</label>
                      <div className="flex items-center space-x-2">
                        <i className="ri-calendar-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                        <span className="text-sm sm:text-base text-gray-900 font-medium">
                          {new Date(viewingEvent.date).toLocaleDateString('de-DE', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <i className="ri-time-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                        <span className="text-sm sm:text-base text-gray-900 font-medium">{viewingEvent.time} Uhr</span>
                      </div>
                    </div>

                    {viewingEvent.automat_name && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Automat</label>
                        <div className="flex items-center space-x-2">
                          <i className="ri-computer-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                          <span className="text-sm sm:text-base text-gray-900 font-medium">{viewingEvent.automat_name}</span>
                        </div>
                      </div>
                    )}

                    {viewingEvent.kontakt_name && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Kontakt</label>
                        <div className="flex items-center space-x-2">
                          <i className="ri-user-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                          <span className="text-sm sm:text-base text-gray-900 font-medium break-words">{viewingEvent.kontakt_name}</span>
                        </div>
                      </div>
                    )}

                    {viewingEvent.assigned_to && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Zugewiesen an</label>
                        <div className="flex items-center space-x-2">
                          <i className="ri-user-settings-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                          <span className="text-sm sm:text-base text-gray-900 font-medium">{viewingEvent.assigned_to}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Geschätzte Dauer</label>
                      <div className="flex items-center space-x-2">
                        <i className="ri-timer-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                        <span className="text-sm sm:text-base text-gray-900 font-medium">{viewingEvent.estimated_duration} Minuten</span>
                      </div>
                    </div>

                    {viewingEvent.recurring && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Wiederholung</label>
                        <div className="flex items-center space-x-2">
                          <i className="ri-repeat-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                          <span className="text-sm sm:text-base text-gray-900 font-medium">
                            {recurringPatterns.find(p => p.value === viewingEvent.recurring_pattern)?.label || 'Wiederkehrend'}
                          </span>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Erstellt</label>
                      <div className="flex items-center space-x-2">
                        <i className="ri-information-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                        <span className="text-sm sm:text-base text-gray-900 font-medium">
                          {new Date().toLocaleDateString('de-DE')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {viewingEvent.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-2">Notizen</label>
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-sm sm:text-base text-gray-900 whitespace-pre-wrap break-words">{viewingEvent.notes}</p>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                  <Button 
                    onClick={() => {
                      setIsViewModalOpen(false);
                      openEditModal(viewingEvent);
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-sm sm:text-base py-3 sm:py-2 touch-manipulation"
                  >
                    <i className="ri-edit-line w-4 h-4 flex items-center justify-center mr-2"></i>
                    Bearbeiten
                  </Button>
                  <Button 
                    variant="secondary"
                    onClick={() => {
                      setIsViewModalOpen(false);
                      handleDelete(viewingEvent.id);
                    }}
                    className="bg-red-50 text-red-600 hover:bg-red-100 border-red-200 text-sm sm:text-base py-3 sm:py-2 touch-manipulation"
                  >
                    <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center mr-2"></i>
                    Löschen
                  </Button>
                  <Button 
                    variant="secondary" 
                    onClick={() => {
                      setIsViewModalOpen(false);
                      setViewingEvent(null);
                    }}
                    className="text-sm sm:text-base py-3 sm:py-2 touch-manipulation"
                  >
                    Schließen
                  </Button>
                </div>
              </div>
            )}
          </Modal>

          {/* Modal für neuen Termin */}
          <Modal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              resetForm();
            }}
            title="Neuen Termin erstellen"
            maxWidth="xl"
          >
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <Input
                  label="Titel"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="z.B. Wartung Automat A1"
                  required
                />
                
                <Select
                  label="Typ"
                  options={eventTypes.map(type => ({ value: type.value, label: type.label }))}
                  value={formData.type}
                  onChange={(value) => handleInputChange('type', value)}
                  required
                />
                
                <Input
                  label="Datum"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  required
                />
                
                <Input
                  label="Zeit"
                  type="time"
                  value={formData.time}
                  onChange={(e) => handleInputChange('time', e.target.value)}
                  required
                />
                
                <Select
                  label="Automat (optional)"
                  options={[
                    { value: '', label: 'Kein Automat' },
                    ...automaten.map(automat => ({ 
                      value: automat.id, 
                      label: `${automat.name} - ${automat.location}`
                    }))
                  ]}
                  value={formData.automat_id}
                  onChange={(value) => handleInputChange('automat_id', value)}
                />
                
                <Select
                  label="Kontakt (optional)"
                  options={[
                    { value: '', label: 'Kein Kontakt' },
                    ...kontakte.map(kontakt => ({ 
                      value: kontakt.id, 
                      label: `${kontakt.name}${kontakt.firma ? ` (${kontakt.firma})` : ''}`
                    }))
                  ]}
                  value={formData.kontakt_id}
                  onChange={(value) => handleInputChange('kontakt_id', value)}
                />
                
                <Select
                  label="Priorität"
                  options={priorities.map(priority => ({ value: priority.value, label: priority.label }))}
                  value={formData.priority}
                  onChange={(value) => handleInputChange('priority', value)}
                  required
                />
                
                <Select
                  label="Status"
                  options={statuses.map(status => ({ value: status.value, label: status.label }))}
                  value={formData.status}
                  onChange={(value) => handleInputChange('status', value)}
                  required
                />
                
                <Input
                  label="Geschätzte Dauer (Min.)"
                  type="number"
                  value={formData.estimated_duration}
                  onChange={(e) => handleInputChange('estimated_duration', e.target.value)}
                  placeholder="60"
                  required
                />
                
                <Input
                  label="Zugewiesen an"
                  value={formData.assigned_to}
                  onChange={(e) => handleInputChange('assigned_to', e.target.value)}
                  placeholder="Name der verantwortlichen Person"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Beschreibung</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                  rows={3}
                  placeholder="Details zum Termin..."
                  required
                />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="recurring"
                    checked={formData.recurring}
                    onChange={(e) => handleInputChange('recurring', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="recurring" className="ml-2 block text-sm text-gray-900">
                    Wiederkehrend
                  </label>
                </div>
                
                {formData.recurring && (
                  <div className="flex-1">
                    <Select
                      options={recurringPatterns}
                      value={formData.recurring_pattern}
                      onChange={(value) => handleInputChange('recurring_pattern', value)}
                      required={formData.recurring}
                    />
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notizen</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                  rows={2}
                  placeholder="Zusätzliche Informationen..."
                />
              </div>
              
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                <Button type="submit" className="flex-1 py-3 sm:py-2 text-sm sm:text-base touch-manipulation">
                  Termin erstellen
                </Button>
                <Button 
                  type="button" 
                  variant="secondary" 
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="py-3 sm:py-2 text-sm sm:text-base touch-manipulation"
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          </Modal>

          {/* Modal für Terminbearbeitung */}
          <Modal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              resetForm();
            }}
            title="Termin bearbeiten"
            maxWidth="xl"
          >
            <form onSubmit={handleEditSubmit} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <Input
                  label="Titel"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="z.B. Wartung Automat A1"
                  required
                />
                
                <Select
                  label="Typ"
                  options={eventTypes.map(type => ({ value: type.value, label: type.label }))}
                  value={formData.type}
                  onChange={(value) => handleInputChange('type', value)}
                  required
                />
                
                <Input
                  label="Datum"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  required
                />
                
                <Input
                  label="Zeit"
                  type="time"
                  value={formData.time}
                  onChange={(e) => handleInputChange('time', e.target.value)}
                  required
                />
                
                <Select
                  label="Automat (optional)"
                  options={[
                    { value: '', label: 'Kein Automat' },
                    ...automaten.map(automat => ({ 
                      value: automat.id, 
                      label: `${automat.name} - ${automat.location}`
                    }))
                  ]}
                  value={formData.automat_id}
                  onChange={(value) => handleInputChange('automat_id', value)}
                />
                
                <Select
                  label="Kontakt (optional)"
                  options={[
                    { value: '', label: 'Kein Kontakt' },
                    ...kontakte.map(kontakt => ({ 
                      value: kontakt.id, 
                      label: `${kontakt.name}${kontakt.firma ? ` (${kontakt.firma})` : ''}`
                    }))
                  ]}
                  value={formData.kontakt_id}
                  onChange={(value) => handleInputChange('kontakt_id', value)}
                />
                
                <Select
                  label="Priorität"
                  options={priorities.map(priority => ({ value: priority.value, label: priority.label }))}
                  value={formData.priority}
                  onChange={(value) => handleInputChange('priority', value)}
                  required
                />
                
                <Select
                  label="Status"
                  options={statuses.map(status => ({ value: status.value, label: status.label }))}
                  value={formData.status}
                  onChange={(value) => handleInputChange('status', value)}
                  required
                />
                
                <Input
                  label="Geschätzte Dauer (Min.)"
                  type="number"
                  value={formData.estimated_duration}
                  onChange={(e) => handleInputChange('estimated_duration', e.target.value)}
                  placeholder="60"
                  required
                />
                
                <Input
                  label="Zugewiesen an"
                  value={formData.assigned_to}
                  onChange={(e) => handleInputChange('assigned_to', e.target.value)}
                  placeholder="Name der verantwortlichen Person"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Beschreibung</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                  rows={3}
                  placeholder="Details zum Termin..."
                  required
                />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="recurring-edit"
                    checked={formData.recurring}
                    onChange={(e) => handleInputChange('recurring', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="recurring-edit" className="ml-2 block text-sm text-gray-900">
                    Wiederkehrend
                  </label>
                </div>
                
                {formData.recurring && (
                  <div className="flex-1">
                    <Select
                      options={recurringPatterns}
                      value={formData.recurring_pattern}
                      onChange={(value) => handleInputChange('recurring_pattern', value)}
                      required={formData.recurring}
                    />
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notizen</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                  rows={2}
                  placeholder="Zusätzliche Informationen..."
                />
              </div>
              
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700 py-3 sm:py-2 text-sm sm:text-base touch-manipulation">
                  Änderungen speichern
                </Button>
                <Button 
                  type="button" 
                  variant="secondary" 
                  onClick={() => {
                    setIsEditModalOpen(false);
                    resetForm();
                  }}
                  className="py-3 sm:py-2 text-sm sm:text-base touch-manipulation"
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          </Modal>
        </div>
      </div>
    </div>
  );
}
